import { Card, CardContent } from '@h2hspark/ui';
import { ArrowRight } from 'lucide-react';
import * as React from 'react';

import { usePublishedCollection } from './api';
import { attributionDetailOf, caseStudyPath, initialsOf, limitedItems } from './constants';
import { ContentLink } from './content-link-panel';
import { Band, SectionLeadIn } from './section-band-panel';
import { list, text, type ContentData, type PublishedContent } from './types';

/**
 * Testimonials and case studies as page sections (`CMS-015`).
 *
 * ## Why these are not in `sections-panel.tsx`
 *
 * Every other section holds its own content: a hero's headline is a field on the hero. These two
 * hold a heading and a limit, and their content is **their own published entries**, read at render
 * time. That makes them the only authored sections that fetch, and a fetching component in the
 * middle of the renderer's switch would make the whole switch untestable without a query client.
 *
 * So each is a pair: a container that asks `api.ts` for the collection, and a presentational
 * component that takes the items as a prop. The presentational half is what the tests render, which
 * is how `CMS-015` AC-5 and AC-6 are assertions about real markup rather than about source text.
 *
 * ## The order is the editor's, not the API's convenience
 *
 * `CMS-003` AC-3 has the delivery route order a collection by the field the *type* declares, and
 * both of these declare `sortOrder`. Nothing here re-sorts: the first testimonial on the page is a
 * choice somebody made in the console (`CMS-015` AC-3), and a second sort on this side would be a
 * second opinion about it.
 *
 * ## Nothing, not an empty heading
 *
 * A collection with no published items renders **nothing at all** — no band, no heading, no
 * whitespace (`CMS-015` AC-5). A heading over an empty grid reads as a page that failed to load
 * rather than as a section nobody has filled in yet, and it is worse on a marketing site than the
 * absence would be. The same is true while the query is in flight: a heading that appears and then
 * vanishes is a layout shift on the most-read page on the site.
 */

// ── Testimonials ─────────────────────────────────────────────────────────────────────────────

export function TestimonialsSection({
  section,
}: {
  readonly section: ContentData;
}): React.ReactElement | null {
  const collection = usePublishedCollection('testimonial');
  return (
    <Testimonials
      eyebrow={text(section, 'eyebrow')}
      heading={text(section, 'heading')}
      intro={text(section, 'intro')}
      items={limitedItems(collection.data ?? [], section['limit'])}
    />
  );
}

export function Testimonials({
  eyebrow,
  heading,
  intro,
  items,
}: {
  readonly eyebrow?: string | undefined;
  readonly heading: string;
  readonly intro?: string | undefined;
  readonly items: ReadonlyArray<PublishedContent>;
}): React.ReactElement | null {
  if (items.length === 0) return null;

  return (
    <Band tint="sunken">
      <SectionLeadIn eyebrow={eyebrow} heading={heading} intro={intro} />
      <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <li key={item.slug}>
            <Card className="h-full">
              <CardContent className="flex h-full flex-col pt-6">
                {/*
                  The prototype opens each card with an oversized quotation mark. It is decoration
                  — `aria-hidden`, and the quote is already in a `blockquote` — but it is the mark
                  that makes a wall of three cards read as three quotations at a glance.
                */}
                <span aria-hidden className="text-4xl font-extrabold leading-none text-primary/25">
                  &ldquo;
                </span>
                {/*
                  **A blockquote with its attribution in a cite** (`CMS-015` AC-6).

                  The prototype draws a quote as a paragraph with a name in a smaller font
                  underneath, which looks right and says nothing: to a screen reader the name is
                  merely the next thing on the page, and there is no way to tell it apart from the
                  start of the following card. `blockquote` + `cite` is what makes the attribution
                  *belong to* the quote, and it costs nothing visually.
                */}
                <blockquote className="mt-2 flex flex-1 flex-col">
                  <p className="flex-1 text-sm leading-relaxed text-foreground">
                    {text(item.data, 'quote')}
                  </p>
                  <footer className="mt-5 flex items-center gap-3 border-t pt-4">
                    <TestimonialFace testimonial={item.data} />
                    <cite className="text-sm not-italic">
                      <span className="block font-semibold">{text(item.data, 'name')}</span>
                      {attributionDetailOf(item.data) !== '' && (
                        <span className="block text-xs text-muted-foreground">
                          {attributionDetailOf(item.data)}
                        </span>
                      )}
                    </cite>
                  </footer>
                </blockquote>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
    </Band>
  );
}

/**
 * The photograph, or the initials of the name.
 *
 * The alt text is empty on purpose: the name is directly beside it in the `cite`, and a photograph
 * captioned with the name it sits next to makes a screen reader say it twice.
 */
function TestimonialFace({
  testimonial,
}: {
  readonly testimonial: ContentData;
}): React.ReactElement {
  const photo = text(testimonial, 'photo');
  if (photo !== '') {
    return <img src={photo} alt="" className="h-11 w-11 flex-none rounded-full object-cover" />;
  }
  return (
    <span
      aria-hidden
      className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
    >
      {initialsOf(text(testimonial, 'name'))}
    </span>
  );
}

// ── Case studies ─────────────────────────────────────────────────────────────────────────────

export function CaseStudiesSection({
  section,
}: {
  readonly section: ContentData;
}): React.ReactElement | null {
  const collection = usePublishedCollection('caseStudy');
  return (
    <CaseStudies
      anchor={text(section, 'anchor')}
      eyebrow={text(section, 'eyebrow')}
      heading={text(section, 'heading')}
      intro={text(section, 'intro')}
      items={limitedItems(collection.data ?? [], section['limit'])}
    />
  );
}

export function CaseStudies({
  anchor,
  eyebrow,
  heading,
  intro,
  items,
}: {
  readonly anchor?: string | undefined;
  readonly eyebrow?: string | undefined;
  readonly heading: string;
  readonly intro?: string | undefined;
  readonly items: ReadonlyArray<PublishedContent>;
}): React.ReactElement | null {
  if (items.length === 0) return null;

  return (
    <Band anchor={anchor}>
      <SectionLeadIn eyebrow={eyebrow} heading={heading} intro={intro} />
      <ul className="flex flex-col gap-6">
        {items.map((item) => (
          <li key={item.slug}>
            <Card>
              {/*
                The card is the prototype's: the argument on the left, the metrics rail on the
                right. The rail is a definition list rather than a grid of divs — each figure is a
                value with a label, which is what a `dl` is for, and "+41%" on its own means
                nothing to somebody who cannot see the words underneath it.
              */}
              <CardContent className="grid gap-8 pt-6 sm:grid-cols-[1.4fr_1fr]">
                <div>
                  {/*
                    **The organization, identified rather than merely named** (`CMS-018` AC-5).
                    The prototype puts a tinted tile of the organization's initials beside its name
                    and format, which is what makes three stacked cards scannable as three
                    different organizers. Initials are derived from the name, not authored —
                    `content-types.ts` drops the prototype's `initials` field on purpose, because
                    a name and its initials are one fact and storing both lets them disagree.
                  */}
                  <div className="flex items-center gap-3">
                    <span
                      aria-hidden
                      className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-md bg-primary/10 text-sm font-bold text-primary"
                    >
                      {initialsOf(text(item.data, 'organizationName'))}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold">
                        {text(item.data, 'organizationName')}
                      </span>
                      {text(item.data, 'format') !== '' && (
                        <span className="block text-xs text-muted-foreground">
                          {text(item.data, 'format')}
                        </span>
                      )}
                    </span>
                  </div>
                  <h3 className="mt-3 text-lg font-semibold">{text(item.data, 'headline')}</h3>
                  {/*
                    **The challenge and what changed, each labelled** (`CMS-018` AC-4).

                    The card used to render `summary` — one unlabelled paragraph — while the entry
                    carried `challenge` and `solution` and the prototype's card printed both under
                    bold "Challenge:" and "What changed:" leaders. That is the failure this whole
                    story is about in miniature: the words on the card were plausible, so a
                    copy-by-copy comparison passed, and the card was still telling a visitor half
                    of what the design meant them to read. `summary` is still the entry's one-line
                    précis and still what the case study's own page and its meta description use.
                  */}
                  <CaseStudyPoint label="Challenge" body={text(item.data, 'challenge')} />
                  <CaseStudyPoint label="What changed" body={text(item.data, 'solution')} />
                  <p className="mt-4 text-sm font-semibold text-primary">
                    <ContentLink href={caseStudyPath(item.slug)} className="inline-flex items-center gap-1.5">
                      Read the full story
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </ContentLink>
                  </p>
                </div>
                <CaseStudyMetrics metrics={list(item.data, 'metrics')} />
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
    </Band>
  );
}

/**
 * One labelled point on a case study card — "**Challenge:** …".
 *
 * The label is component vocabulary rather than content: it names a field the content type
 * declares, so an editor who could retitle it would be renaming a field from inside one card. The
 * case study's own page labels the same two fields with the same two words, which is the point of
 * putting them in one component — a card and a page disagreeing about what `solution` is called is
 * the kind of drift nobody notices until a client reads both.
 *
 * An empty body renders nothing at all: a bold "Challenge:" with no sentence after it is worse
 * than the absence.
 */
function CaseStudyPoint({
  label,
  body,
}: {
  readonly label: string;
  readonly body: string;
}): React.ReactElement | null {
  if (body === '') return null;
  return (
    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
      <strong className="font-semibold text-foreground">{label}: </strong>
      {body}
    </p>
  );
}

export function CaseStudyMetrics({
  metrics,
}: {
  readonly metrics: ReadonlyArray<ContentData>;
}): React.ReactElement | null {
  if (metrics.length === 0) return null;
  return (
    <dl className="flex flex-col justify-center gap-5 rounded-lg bg-muted/40 p-6">
      {metrics.map((metric, index) => (
        // `flex-col-reverse` so the figure sits above its label visually while the label stays
        // first in the document. The alternative — a visually-hidden `dt` and a decorative
        // paragraph repeating it — puts the same words in the page twice and is how a screen
        // reader ends up reading the label, then the figure, then the label again.
        <div key={index} className="flex flex-col-reverse">
          <dt className="mt-1 text-sm text-muted-foreground">{text(metric, 'label')}</dt>
          <dd className="text-2xl font-bold leading-none text-primary">{text(metric, 'value')}</dd>
        </div>
      ))}
    </dl>
  );
}
